import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Music, TrendingUp, Plus, Smartphone, Speaker, Video, HelpCircle, Users, Sliders } from "lucide-react";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/modules/calculator";
import type { ArtistWithShows, ShowWithDetails } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FaSpotify, FaInstagram, FaTiktok } from "react-icons/fa";
import { ShowListItem } from "@/components/show-list-item";
import { ShowListHeader } from "@/components/show-list-header";
import ShowDetail from "@/components/show-detail";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { PlanStorage, type PackageTier } from "@/lib/plan-storage";
import { usePlanStore } from "@/stores/plan-store";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { usePlanSidebar } from "@/contexts/plan-sidebar-context";
import { BrowseLayout } from "@/components/layouts/browse-layout";
import { calculateAudienceMatch } from "@/lib/audience-matching";
import { trimShowsByFilters, type ShowFilterConfig } from "@shared/filter-engine";
import { type PackageType, getPackageConfig } from "@/lib/package-config";
import { PRICING_MULTIPLIERS } from "@/lib/package-config";
import { PackageCard } from "@/components/package-card";
import { FeatureTooltip } from "@/components/feature-tooltip";
import { CustomPackageBuilderModal } from "@/components/custom-package-builder-modal";
import { CustomPackageCard } from "@/components/custom-package-card";
import {
  DetailPageHeader,
  DetailPageLoading,
  DetailPageError,
  StickyPricingBar,
  PricingSection,
  ShowDetailModal,
} from "@/components/detail-pages";

export default function ArtistDetailPage() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { isPricingVisible } = usePricingVisibility();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedShows, setSelectedShows] = useState<Set<number>>(new Set());
  const [viewingShow, setViewingShow] = useState<ShowWithDetails | null>(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [showStickyPricing, setShowStickyPricing] = useState(false);
  const { open: openPlanSidebar } = usePlanSidebar();
  const [expandedPackage, setExpandedPackage] = useState<PackageType | null>(null);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const pricingRef = useRef<HTMLDivElement>(null);

  // Subscribe only to planPackages for backward compatibility (needed elsewhere in component)
  const planPackages = usePlanStore(state => state.getPackages(user?.id));
  // Direct state access for better reactivity
  const customPackage = usePlanStore(state => {
    const userKey = user?.id ?? 0;
    return state.customPackages[userKey] || null;
  });
  const hasCustomPackage = customPackage !== null && (customPackage?.selectedDeliverables?.length ?? 0) > 0;
  const setCustomPackageDeliverables = usePlanStore(state => state.setCustomPackageDeliverables);
  const clearCustomPackage = usePlanStore(state => state.clearCustomPackage);
  const pricingCardsRef = useRef<HTMLDivElement>(null);
  const hasAutoSelectedRef = useRef<string | null>(null); // Track if we've auto-selected for this artist

  // Note: No need for event listeners! Zustand automatically re-renders when plan packages change

  // Helper function to toggle package dropdown
  const togglePackageDropdown = (packageType: PackageType) => {
    setExpandedPackage(expandedPackage === packageType ? null : packageType);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (expandedPackage && !target.closest('.package-dropdown-container')) {
        setExpandedPackage(null);
      }
    };

    if (expandedPackage) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedPackage]);

  // Fetch artist data
  const { data: artist, isLoading, error } = useQuery({
    queryKey: [`/api/artists/${id}/with-shows`],
    queryFn: async () => {
      const response = await apiRequest(`/api/artists/${id}/with-shows`);
      return response as ArtistWithShows;
    },
    enabled: !!id,
    staleTime: 0, // Force refetch
    gcTime: 0, // Don't cache
  });

  // Reset state when artist changes
  useEffect(() => {
    setViewingShow(null);
    setSelectedShows(new Set());
    setIsBioExpanded(false);
    setShowStickyPricing(false);
    hasAutoSelectedRef.current = null; // Reset auto-selection tracking
  }, [id]);

  // Auto-select shows that are already in the plan (only on initial load)
  useEffect(() => {
    if (artist?.upcomingShows && user?.id && artist.id && hasAutoSelectedRef.current !== artist.id.toString()) {
      const showsInPlan = new Set<number>();
      
      artist.upcomingShows.forEach(show => {
        if (PlanStorage.isShowInPlan(show.id, user.id)) {
          showsInPlan.add(show.id);
        }
      });
      
      // Auto-select plan shows and mark that we've done this for this artist
      if (showsInPlan.size > 0) {
        setSelectedShows(showsInPlan);
        hasAutoSelectedRef.current = artist.id.toString();
      }
    }
  }, [artist?.upcomingShows, artist?.id, user?.id]);

  // Handle scroll to show/hide sticky pricing
  useEffect(() => {
    const handleScroll = () => {
      if (pricingCardsRef.current && selectedShows.size > 0) {
        const rect = pricingCardsRef.current.getBoundingClientRect();
        // Show sticky when the bottom of pricing cards goes out of view
        setShowStickyPricing(rect.bottom < 0);
      } else if (selectedShows.size === 0) {
        setShowStickyPricing(false);
      }
    };

    // Run on scroll
    window.addEventListener('scroll', handleScroll);

    // Also run immediately when isPricingVisible changes to re-evaluate sticky bar
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedShows.size, isPricingVisible]);

  // Parse URL filters for display and filtering
  const urlFilters = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const careerStageParam = params.get('careerStage');
    const venueTypeParam = params.get('venueType');
    return {
      location: params.get('location') || '',
      genre: params.get('genre') || '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || '',
      artistName: params.get('artistName') || '',
      venueName: params.get('venueName') || '',
      careerStage: careerStageParam ? careerStageParam.split(',') : [],
      venueType: venueTypeParam ? venueTypeParam.split(',') : [],
      setArtist: params.get('setArtist') || '',
    };
  }, []);

  // Check if there are any active filters to display
  const hasActiveFilters = useMemo(() => {
    return !!(urlFilters.location || urlFilters.genre || urlFilters.dateFrom || urlFilters.dateTo || urlFilters.artistName || urlFilters.venueName || (urlFilters.careerStage && urlFilters.careerStage.length > 0) || (urlFilters.venueType && urlFilters.venueType.length > 0) || activePersona);
  }, [urlFilters, activePersona]);

  // Apply show-level filtering only (DMA, location, date)
  // Artist-level filtering (genre, demographics) is intentionally REMOVED on detail pages
  // because all shows belong to the same artist who already passed persona validation on browse
  const filteredAndSortedShows = useMemo(() => {
    if (!artist?.upcomingShows) return [];

    let shows = [...artist.upcomingShows];

    // Apply show-level URL filters (location, date range, venue name, venue type)
    if (urlFilters.location || urlFilters.dateFrom || urlFilters.dateTo || urlFilters.venueName || (urlFilters.venueType && urlFilters.venueType.length > 0)) {
      const showConfig: ShowFilterConfig = {
        dateFrom: urlFilters.dateFrom || undefined,
        dateTo: urlFilters.dateTo || undefined,
        location: urlFilters.location || undefined,
        venueName: urlFilters.venueName || undefined,
        venueType: urlFilters.venueType && urlFilters.venueType.length > 0 ? urlFilters.venueType : undefined,
      };
      shows = trimShowsByFilters(shows, showConfig, { excludePastShows: false });
    }

    // KEEP: DMA hard filtering - show-level attribute (venue's market)
    if (activePersona?.targetDmaIds && activePersona.targetDmaIds.length > 0) {
      shows = shows.filter(show => {
        const venueDmaId = show.venue?.dmaId;
        return venueDmaId !== null && venueDmaId !== undefined && activePersona.targetDmaIds!.includes(venueDmaId);
      });
    }

    // REMOVED: Genre filtering - artist-level attribute, all shows have same artist
    // REMOVED: Demographic filtering - artist-level, redundant since all shows have same artist
    // The artist already passed persona validation on the browse page

    // Sort by date (soonest first), then artist name
    return shows.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      if (dateA !== dateB) return dateA - dateB;
      const nameA = (a.artist?.name || '').toLowerCase();
      const nameB = (b.artist?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [artist?.upcomingShows, activePersona?.targetDmaIds, urlFilters]);

  // Use filtered shows for all calculations
  const displayShows = filteredAndSortedShows;
  const sponsorableShows = displayShows.filter(show => show.sponsorable ?? true);

  // Calculate package counts - read directly from store without subscription
  const packageCounts = useMemo(() => {
    const selectedShowIds = Array.from(selectedShows);

    // Read directly from store state (no subscription)
    const state = usePlanStore.getState();
    const userKey = user?.id ?? 0;
    const planShowsObj = state.shows[userKey] || {};
    const planPackagesObj = state.packages[userKey] || {};

    const showIdsInPlan = Object.keys(planShowsObj).map(Number);
    const inPlanShowIds = new Set(showIdsInPlan);

    // Shows selected but not yet in plan
    const newlySelectedShowIds = selectedShowIds.filter(id => !inPlanShowIds.has(id));
    const newlySelectedCount = newlySelectedShowIds.length;

    // Count committed shows by tier - ONLY for the current artist
    let setCoreCommitted = 0;
    let setAudioCommitted = 0;
    let setVideoCommitted = 0;

    showIdsInPlan.forEach(showId => {
      // Get the show from the plan
      const planShow = planShowsObj[showId];

      // Only count this show if it belongs to the current artist
      if (planShow && artist && planShow.artistId === artist.id) {
        const tier = planPackagesObj[showId]?.tier;

        // Only count standard package tiers, not custom packages
        if (tier === 'set-core') setCoreCommitted++;
        else if (tier === 'set-audio') setAudioCommitted++;
        else if (tier === 'set-video') setVideoCommitted++;
        // Note: 'custom' tier is intentionally not counted in any package
      }
    });

    return {
      'set-core': {
        committed: setCoreCommitted,
        selected: newlySelectedCount,
        total: setCoreCommitted + newlySelectedCount
      },
      'set-audio': {
        committed: setAudioCommitted,
        selected: newlySelectedCount,
        total: setAudioCommitted + newlySelectedCount
      },
      'set-video': {
        committed: setVideoCommitted,
        selected: newlySelectedCount,
        total: setVideoCommitted + newlySelectedCount
      }
    };
  }, [selectedShows, user?.id, artist?.id]);

  // Selection state calculations
  const selectedCount = selectedShows.size;
  const allSponsorableSelected = sponsorableShows.length > 0 && sponsorableShows.every(show => selectedShows.has(show.id));

  // Handle select all functionality
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const sponsorableIds = new Set(sponsorableShows.map(show => show.id));
      setSelectedShows(sponsorableIds);
    } else {
      setSelectedShows(new Set());
    }
  };

  // Format price helper
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Pricing calculation moved after displayShows declaration

  const handleAddToProposal = (show: ShowWithDetails) => {
    const wasAdded = PlanStorage.addShow(show, user?.id);
    
    if (wasAdded) {
      toast({
        title: "Added to Proposal",
        description: `${show.artist.name} show on ${formatDate(show.date)} added to your proposal.`,
      });
    } else {
      toast({
        title: "Already in Proposal",
        description: `${show.artist.name} show on ${formatDate(show.date)} is already in your proposal.`,
        variant: "destructive",
      });
    }
  };

  const handleSaveCustomPackage = (deliverables: string[], basePackageTier: 'set-core' | 'set-audio' | 'set-video') => {
    setCustomPackageDeliverables(deliverables, basePackageTier, user?.id);
  };

  const handleClearCustomPackage = () => {
    clearCustomPackage(user?.id);
    toast({
      title: "Custom Package Cleared",
      description: "Your custom package has been removed.",
    });
  };

  const handleAddCustomToProposal = () => {
    handleAddPackageToProposal('custom', 0); // Custom packages have no calculated price
  };

  const handleAddPackageToProposal = (packageType: PackageType, packagePrice: number) => {
    if ((packageType !== 'custom' && !calculatorPricing) || selectedShows.size === 0) {
      toast({
        title: "No Shows Selected",
        description: "Please select shows before adding a package to your proposal.",
        variant: "destructive",
      });
      return;
    }

    const selectedShowsList = displayShows.filter((show: ShowWithDetails) => selectedShows.has(show.id));

    // Filter out shows that are already in the plan to avoid re-adding them
    const showsNotInPlan = selectedShowsList.filter(show => !PlanStorage.isShowInPlan(show.id, user?.id));

    if (showsNotInPlan.length === 0) {
      toast({
        title: "Already in Proposal",
        description: "All selected shows are already in your proposal.",
        variant: "destructive",
      });
      return;
    }

    // Important: Divide price only by the shows we're actually adding (not including shows already in plan)
    // The initial pricePerShow will be recalculated by updateDynamicPricing based on this package tier's show count
    const pricePerShow = Math.round(packagePrice / showsNotInPlan.length);

    // Convert PackageType to PackageTier for PlanStorage compatibility
    const packageTier: PackageTier = packageType;

    const addedCount = PlanStorage.addShowsWithPackage(
      showsNotInPlan,  // Only add new shows, not those already in plan
      packageTier,
      pricePerShow,
      user?.id
    );

    if (addedCount > 0) {
      const tierLabel = packageType === 'set-core' ? 'SET Core' : packageType === 'set-audio' ? 'SET Audio' : 'SET Video';

      // Clear selection after adding to plan
      setSelectedShows(new Set());

      // Open the plan sidebar when shows are added
      openPlanSidebar();

      toast({
        title: "Added to Proposal",
        description: `${addedCount} show(s) added with ${tierLabel} package.`,
      });
    }
  };

  const handleViewShow = (show: ShowWithDetails) => {
    setViewingShow(show);
  };

  const handleBackToList = () => {
    setViewingShow(null);
  };

  const handleShowSelection = (showId: number, checked: boolean) => {
    const newSelection = new Set(selectedShows);
    if (checked) {
      newSelection.add(showId);
    } else {
      newSelection.delete(showId);
    }
    setSelectedShows(newSelection);
  };


  // Calculate total pricing for selected shows using the actual calculator API
  const [calculatorPricing, setCalculatorPricing] = useState<{
    setVideo: number;
    setAudio: number;
    setCore: number;
    setVideoShowCount: number;
    setAudioShowCount: number;
    setCoreShowCount: number;
  } | null>(null);
  const [isCalculatingPricing, setIsCalculatingPricing] = useState(false);


  // Effect to call calculator API when selected shows change or package counts change
  useEffect(() => {
    const calculatePricing = async () => {
      // Only calculate if there are selected shows or committed shows in plan
      const hasShows = selectedShows.size > 0 ||
        packageCounts['set-core'].committed > 0 ||
        packageCounts['set-audio'].committed > 0 ||
        packageCounts['set-video'].committed > 0;

      if (!hasShows || !displayShows || displayShows.length === 0 || !artist) {
        setCalculatorPricing(null);
        return;
      }

      // Get all shows involved (both selected and in plan)
      const allShowIds = new Set([...selectedShows]);

      // Add shows already in plan for this artist
      const state = usePlanStore.getState();
      const userKey = user?.id ?? 0;
      const planShowsObj = state.shows[userKey] || {};
      Object.keys(planShowsObj).forEach(showIdStr => {
        const showId = Number(showIdStr);
        // Check if this show belongs to the current artist
        const show = displayShows.find((s: any) => s.id === showId);
        if (show) {
          allShowIds.add(showId);
        }
      });

      const allInvolvedShows = displayShows.filter((show: any) => allShowIds.has(show.id));

      if (allInvolvedShows.length === 0) {
        setCalculatorPricing(null);
        return;
      }

      setIsCalculatingPricing(true);

      try {
        // Use average venue capacity and market size for the calculation
        const avgCapacity = allInvolvedShows.reduce((sum: number, show: any) => sum + (show.venue.capacity || 1000), 0) / allInvolvedShows.length;

        // Get market size from first show's venue (they should all be similar for the same artist)
        const firstShow = allInvolvedShows[0];
        const dmaPopulation = firstShow?.venue?.market?.population;

        const getMarketSize = (dmaPopulation?: number | null) => {
          if (!dmaPopulation) return 'Medium';
          if (dmaPopulation >= 2000000) return 'Large';
          if (dmaPopulation >= 700000) return 'Medium';
          return 'Small';
        };

        const marketSize = getMarketSize(dmaPopulation);
        // Normalize career stage for calculator API - handle both "Break-through" and "Developing/Local"
        const rawCareerStage = artist.careerStage || 'Established 2';
        // Ensure rawCareerStage is a string before calling replace
        const careerStageStr = String(rawCareerStage);
        let careerStage = careerStageStr.replace(/Break-[Tt]hrough\//i, '');

        // Handle Developing/Local - normalize to "Developing"
        if (careerStage.toLowerCase().includes('developing') || careerStage.toLowerCase().includes('local')) {
          careerStage = 'Developing';
        }

        // Calculate pricing for each package type separately based on their totals
        const setCoreShowCount = packageCounts['set-core'].total;
        const setAudioShowCount = packageCounts['set-audio'].total;
        const setVideoShowCount = packageCounts['set-video'].total;

        // Make API calls for each package type if they have shows
        const pricingResults = await Promise.all([
          // SET Video pricing
          setVideoShowCount > 0 ? apiRequest('/api/calculator/calculate', {
            method: 'POST',
            body: {
              careerStage,
              numberOfShows: setVideoShowCount,
              mediaObjective: 'No Media',
              marketSize,
              venueCapacity: Math.round(avgCapacity),
            },
          }) : null,
          // SET Audio pricing
          setAudioShowCount > 0 ? apiRequest('/api/calculator/calculate', {
            method: 'POST',
            body: {
              careerStage,
              numberOfShows: setAudioShowCount,
              mediaObjective: 'No Media',
              marketSize,
              venueCapacity: Math.round(avgCapacity),
            },
          }) : null,
          // SET Core pricing
          setCoreShowCount > 0 ? apiRequest('/api/calculator/calculate', {
            method: 'POST',
            body: {
              careerStage,
              numberOfShows: setCoreShowCount,
              mediaObjective: 'No Media',
              marketSize,
              venueCapacity: Math.round(avgCapacity),
            },
          }) : null,
        ]);

        const [setVideoData, setAudioData, setCoreData] = pricingResults;

        // Calculate pricing with fallback logic
        const setVideoPrice = setVideoData?.packagePricing?.setVideo || (setVideoData?.estimatedCost?.base ? Math.round(setVideoData.estimatedCost.base * PRICING_MULTIPLIERS.SET_VIDEO) : 0);
        const setAudioPrice = setAudioData?.packagePricing?.setAudio || (setAudioData?.estimatedCost?.base ? Math.round(setAudioData.estimatedCost.base * PRICING_MULTIPLIERS.SET_AUDIO) : 0);
        const setCorePrice = setCoreData?.packagePricing?.setCore || (setCoreData?.estimatedCost?.base ? Math.round(setCoreData.estimatedCost.base * PRICING_MULTIPLIERS.SET_CORE) : 0);

        const pricing = {
          setVideo: setVideoPrice,
          setAudio: setAudioPrice,
          setCore: setCorePrice,
          setVideoShowCount,
          setAudioShowCount,
          setCoreShowCount,
        };

        setCalculatorPricing(pricing);
      } catch (error) {
        console.error('Error calculating pricing:', error);
        setCalculatorPricing(null);
      } finally {
        setIsCalculatingPricing(false);
      }
    };

    calculatePricing();
  }, [selectedShows, displayShows, artist, packageCounts, user?.id]);
  
  // Use calculator pricing instead of manual calculation
  const calculateTotalPricing = calculatorPricing;

  if (isLoading) {
    return <DetailPageLoading />;
  }

  if (error || !artist) {
    return (
      <DetailPageError
        title="Artist Not Found"
        message="The artist you're looking for doesn't exist or has been removed."
        backHref="/browse-artists"
        backText="Back to Browse Artists"
      />
    );
  }


  // Helper function to get audience match data for a show when persona is active
  const getAudienceMatchData = (show: ShowWithDetails) => {
    if (!activePersona || !user) return { percentage: 0, matchingAttributes: [] };
    
    const hasPersonaFilters = Object.values({
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    }).some(arr => arr.length > 0);

    if (!hasPersonaFilters) return { percentage: 0, matchingAttributes: [] };

    const targetAudience = {
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    };

    // Use the page-level artist object (has full demographics from direct DB fetch)
    // instead of show.artist (lightweight, no demographic JSONB fields from getShows())
    return calculateAudienceMatch(artist, targetAudience);
  };

  return (
    <BrowseLayout>
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Sticky Pricing Bar */}
      {showStickyPricing && calculateTotalPricing && isPricingVisible && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-gray-100 text-gray-700">
                  {selectedShows.size} show{selectedShows.size !== 1 ? 's' : ''} selected
                </Badge>
                <span className="text-sm text-gray-600">for {artist?.name}</span>
              </div>
              
              <div className="flex items-center gap-3">
                {/* SET Core Mini Card */}
                <div className="relative package-dropdown-container">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 via-slate-100/50 to-slate-200/30">
                    <div className="flex flex-col items-center">
                      <Smartphone className="w-5 h-5 text-gray-600" />
                      <button
                        onClick={() => togglePackageDropdown('set-core')}
                        className="mt-1 p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${expandedPackage === 'set-core' ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-bold text-gray-600">
                        {formatPrice(calculateTotalPricing.setCore)}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {calculateTotalPricing.setCoreShowCount > 0 ? formatPrice(Math.round(calculateTotalPricing.setCore / calculateTotalPricing.setCoreShowCount)) : '$0'}/show
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddPackageToProposal('set-core', calculateTotalPricing.setCore)}
                      className="h-6 px-2 text-xs border-gray-300"
                    >
                      <Plus className="w-3 h-3" />
                      <span className="text-lg -ml-2">({calculateTotalPricing.setCoreShowCount})</span>
                    </Button>
                  </div>

                  {/* SET Core Dropdown */}
                  {expandedPackage === 'set-core' && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                      <div className="text-sm font-medium text-gray-900 mb-2">SET Core Package Features</div>
                      <div className="space-y-1">
                        {getPackageConfig('set-core').features.map((feature, index) => (
                          <div key={index} className="flex items-start text-xs text-gray-600">
                            <span className="text-gray-400 mr-2 mt-0.5">•</span>
                            <FeatureTooltip
                              text={feature.text}
                              title={feature.details?.title}
                              description={feature.details?.description}
                              imageUrl={feature.details?.imageUrl}
                              isDynamic={feature.isDynamic}
                              dynamicText={feature.dynamicText}
                              selectedShowCount={calculateTotalPricing.setCoreShowCount}
                              className="text-xs"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* SET Audio Mini Card */}
                <div className="relative package-dropdown-container">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-200/30">
                    <div className="flex flex-col items-center">
                      <Speaker className="w-5 h-5 text-blue-600" />
                      <button
                        onClick={() => togglePackageDropdown('set-audio')}
                        className="mt-1 p-0.5 hover:bg-blue-100 rounded transition-colors"
                      >
                        <ChevronDown className={`w-3 h-3 text-blue-500 transition-transform ${expandedPackage === 'set-audio' ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-bold text-blue-600">
                        {formatPrice(calculateTotalPricing.setAudio)}
                      </div>
                      <div className="text-[11px] text-blue-500">
                        {calculateTotalPricing.setAudioShowCount > 0 ? formatPrice(Math.round(calculateTotalPricing.setAudio / calculateTotalPricing.setAudioShowCount)) : '$0'}/show
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleAddPackageToProposal('set-audio', calculateTotalPricing.setAudio)}
                      className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700 border-0"
                    >
                      <Plus className="w-3 h-3" />
                      <span className="text-lg -ml-2">({calculateTotalPricing.setAudioShowCount})</span>
                    </Button>
                  </div>

                  {/* SET Audio Dropdown */}
                  {expandedPackage === 'set-audio' && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-blue-200 rounded-lg shadow-lg z-50 p-3">
                      <div className="text-sm font-medium text-blue-900 mb-2">SET Audio Package Features</div>
                      <div className="space-y-1">
                        {getPackageConfig('set-audio').features.map((feature, index) => (
                          <div key={index} className="flex items-start text-xs text-gray-600">
                            <span className={`mr-2 mt-0.5 ${feature.highlight ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>•</span>
                            <FeatureTooltip
                              text={feature.text}
                              title={feature.details?.title}
                              description={feature.details?.description}
                              imageUrl={feature.details?.imageUrl}
                              isDynamic={feature.isDynamic}
                              dynamicText={feature.dynamicText}
                              selectedShowCount={calculateTotalPricing.setAudioShowCount}
                              className={`text-xs ${feature.highlight ? 'text-blue-600 font-medium' : ''}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* SET Video Mini Card */}
                <div className="relative package-dropdown-container">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-500/15 to-blue-500/15">
                    <div className="flex flex-col items-center">
                      <Video className="w-5 h-5 text-purple-600" />
                      <button
                        onClick={() => togglePackageDropdown('set-video')}
                        className="mt-1 p-0.5 hover:bg-purple-100 rounded transition-colors"
                      >
                        <ChevronDown className={`w-3 h-3 text-purple-500 transition-transform ${expandedPackage === 'set-video' ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-bold text-purple-600">
                        {formatPrice(calculateTotalPricing.setVideo)}
                      </div>
                      <div className="text-[11px] text-purple-500">
                        {calculateTotalPricing.setVideoShowCount > 0 ? formatPrice(Math.round(calculateTotalPricing.setVideo / calculateTotalPricing.setVideoShowCount)) : '$0'}/show
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddPackageToProposal('set-video', calculateTotalPricing.setVideo)}
                      className="h-6 px-2 text-xs bg-purple-600 hover:bg-purple-700 border-0"
                    >
                      <Plus className="w-3 h-3" />
                      <span className="text-lg -ml-2">({calculateTotalPricing.setVideoShowCount})</span>
                    </Button>
                  </div>

                  {/* SET Video Dropdown */}
                  {expandedPackage === 'set-video' && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-purple-200 rounded-lg shadow-lg z-50 p-3">
                      <div className="text-sm font-medium text-purple-900 mb-2">SET Video Package Features</div>
                      <div className="space-y-1">
                        {getPackageConfig('set-video').features.map((feature, index) => (
                          <div key={index} className="flex items-start text-xs text-gray-600">
                            <span className={`mr-2 mt-0.5 ${feature.highlight ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>•</span>
                            <FeatureTooltip
                              text={feature.text}
                              title={feature.details?.title}
                              description={feature.details?.description}
                              imageUrl={feature.details?.imageUrl}
                              isDynamic={feature.isDynamic}
                              dynamicText={feature.dynamicText}
                              selectedShowCount={calculateTotalPricing.setVideoShowCount}
                              className={`text-xs ${feature.highlight ? 'text-purple-600 font-medium' : ''}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sticky Filter Bar with Back Button */}
      <UnifiedFilterBar
        readOnly
        currentMethod="artists"
        filters={{
          location: urlFilters.location || '',
          genre: urlFilters.genre || '',
          dateFrom: urlFilters.dateFrom || '',
          dateTo: urlFilters.dateTo || '',
          artistName: urlFilters.artistName || '',
          venueName: urlFilters.venueName || '',
          careerStage: urlFilters.careerStage,
          venueType: urlFilters.venueType,
          setArtist: urlFilters.setArtist,
        }}
        backLink={`/browse-artists${window.location.search}`}
        backLabel="Back to Browse Artists"
      />

      {/* Artist Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Artist Image */}
          <div className="flex-shrink-0">
            <div className="w-48 h-48 bg-gradient-to-br from-purple-100 to-pink-200 rounded-lg flex items-center justify-center">
              {artist.imageUrl ? (
                <img 
                  src={artist.imageUrl} 
                  alt={artist.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-6xl">🎤</div>
              )}
            </div>
          </div>

          {/* Artist Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h1 className="text-3xl font-bold">{artist.name}</h1>
                  {artist.setArtistGroupId && (
                    <img src="/set.ico" alt="SET" className="w-7 h-7 rounded-sm flex-shrink-0" />
                  )}
                </div>
                
                {/* Genre and Career Stage */}
                <div className="space-y-2 mb-4">
                  {artist?.genres && artist.genres.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Music className="w-4 h-4 text-gray-500" />
                      <div className="flex flex-wrap gap-2">
                        {artist.genres.slice(0, 3).map((genre: string, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {artist?.careerStage && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <Badge variant="outline" className="text-xs">
                        {artist.careerStage}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Social Media Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {artist?.spotifyFollowers && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <FaSpotify className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {artist.spotifyFollowers >= 1000000 
                        ? `${(artist.spotifyFollowers / 1000000).toFixed(1)}M` 
                        : artist.spotifyFollowers >= 1000 
                        ? `${(artist.spotifyFollowers / 1000).toFixed(0)}K`
                        : artist.spotifyFollowers.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">followers</div>
                  </div>
                </div>
              )}
              
              {artist?.spotifyMonthlyListeners && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <FaSpotify className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {artist.spotifyMonthlyListeners >= 1000000 
                        ? `${(artist.spotifyMonthlyListeners / 1000000).toFixed(1)}M` 
                        : artist.spotifyMonthlyListeners >= 1000 
                        ? `${(artist.spotifyMonthlyListeners / 1000).toFixed(0)}K`
                        : artist.spotifyMonthlyListeners.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">monthly listeners</div>
                  </div>
                </div>
              )}
              
              {artist?.instagramFollowers && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <FaInstagram className="w-5 h-5 text-pink-600" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {artist.instagramFollowers >= 1000000 
                        ? `${(artist.instagramFollowers / 1000000).toFixed(1)}M` 
                        : artist.instagramFollowers >= 1000 
                        ? `${(artist.instagramFollowers / 1000).toFixed(0)}K`
                        : artist.instagramFollowers.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">followers</div>
                  </div>
                </div>
              )}
              
              {artist?.tiktokFollowers && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <FaTiktok className="w-5 h-5 text-gray-900" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {artist.tiktokFollowers >= 1000000 
                        ? `${(artist.tiktokFollowers / 1000000).toFixed(1)}M` 
                        : artist.tiktokFollowers >= 1000 
                        ? `${(artist.tiktokFollowers / 1000).toFixed(0)}K`
                        : artist.tiktokFollowers.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">followers</div>
                  </div>
                </div>
              )}
            </div>

            {/* Social Media */}
            {/* Temporarily disabled due to type issues
            {(artist?.spotifyId || artist?.instagramUsername || artist?.tiktokUsername) && (
              <div className="mb-4">
                <SocialMediaWidget artist={artist!} />
              </div>
            )}
            */}
          </div>
        </div>

        {/* Bio */}
        {artist?.bio && (
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">About {artist?.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsBioExpanded(!isBioExpanded)}
              >
                {isBioExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
            <p className={`text-gray-600 ${isBioExpanded ? '' : 'line-clamp-3'}`}>
              {artist?.bio}
            </p>
          </div>
        )}
      </div>

      {/* Dynamic Pricing Section */}
      {user && sponsorableShows.length > 0 && (
        <div ref={pricingRef} className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h2 className="text-3xl font-archivo font-black text-max-primary tracking-tight">
                MAXtimate™ Package Pricing
              </h2>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-max-primary/70 hover:text-max-primary transition-colors">
                    <HelpCircle className="w-5 h-5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-max-primary">MAXtimate™ Package Pricing</h4>
                    <p className="text-sm text-muted-foreground">
                      {calculateTotalPricing ? (
                        <>Package-specific pricing based on shows committed and selected for {artist?.name}.</>
                      ) : (
                        <>Select shows below to see dynamic pricing calculations with {artist?.name}.</>
                      )}
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• SET Core: Basic sponsorship package</li>
                      <li>• SET Audio: Enhanced visibility package</li>
                      <li>• SET Video: Premium sponsorship package</li>
                      {calculateTotalPricing && (
                        <li className="font-medium text-gray-700">• Each package priced based on its own show count (committed + selected)</li>
                      )}
                      {calculateTotalPricing && (calculateTotalPricing.setCoreShowCount > 1 || calculateTotalPricing.setAudioShowCount > 1 || calculateTotalPricing.setVideoShowCount > 1) && (
                        <li>• Volume pricing applied per package tier</li>
                      )}
                    </ul>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            {calculateTotalPricing ? (
              <p className="text-muted-foreground">
                Package-specific pricing shown below
                {(calculateTotalPricing.setCoreShowCount > 1 || calculateTotalPricing.setAudioShowCount > 1 || calculateTotalPricing.setVideoShowCount > 1) && (
                  <span className="text-green-600 font-medium ml-1">
                    (Multi-show volume pricing included)
                  </span>
                )}
                {isCalculatingPricing && (
                  <span className="text-blue-600 font-medium ml-1">
                    (Calculating...)
                  </span>
                )}
              </p>
            ) : (
              <div className="text-muted-foreground">
                <p className="mb-1">Select one or more shows below to see dynamic pricing calculations</p>
                <p className="text-sm text-gray-500">Multi-show volume pricing available</p>
              </div>
            )}
          </div>

          {/* Pricing Tiers Grid */}
          <div ref={pricingCardsRef} className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto mt-8">
            <PackageCard
              packageType="set-core"
              price={calculateTotalPricing?.setCore || 0}
              pricePerShow={calculateTotalPricing?.setCoreShowCount ? Math.round(calculateTotalPricing.setCore / calculateTotalPricing.setCoreShowCount) : 0}
              selectedShowCount={packageCounts['set-core'].total}
              onAddToProposal={handleAddPackageToProposal}
              disabled={!user || selectedShows.size === 0 || !calculateTotalPricing}
              isCalculating={isCalculatingPricing}
              showPricing={isPricingVisible}
            />

            <PackageCard
              packageType="set-audio"
              price={calculateTotalPricing?.setAudio || 0}
              pricePerShow={calculateTotalPricing?.setAudioShowCount ? Math.round(calculateTotalPricing.setAudio / calculateTotalPricing.setAudioShowCount) : 0}
              selectedShowCount={packageCounts['set-audio'].total}
              onAddToProposal={handleAddPackageToProposal}
              disabled={!user || selectedShows.size === 0 || !calculateTotalPricing}
              isCalculating={isCalculatingPricing}
              showPricing={isPricingVisible}
            />

            <PackageCard
              packageType="set-video"
              price={calculateTotalPricing?.setVideo || 0}
              pricePerShow={calculateTotalPricing?.setVideoShowCount ? Math.round(calculateTotalPricing.setVideo / calculateTotalPricing.setVideoShowCount) : 0}
              selectedShowCount={packageCounts['set-video'].total}
              onAddToProposal={handleAddPackageToProposal}
              disabled={!user || selectedShows.size === 0 || !calculateTotalPricing}
              isCalculating={isCalculatingPricing}
              showPricing={isPricingVisible}
            />
          </div>

          {/* Custom Package Section */}
          {hasCustomPackage && customPackage ? (
            <div className="mt-8 max-w-4xl mx-auto">
              <CustomPackageCard
                selectedDeliverables={customPackage.selectedDeliverables}
                selectedShowCount={selectedCount}
                basePackageTier={customPackage.basePackageTier}
                onEdit={() => setIsCustomModalOpen(true)}
                onAddToProposal={handleAddCustomToProposal}
                onClear={handleClearCustomPackage}
                disabled={!user || selectedCount === 0}
              />
            </div>
          ) : (
            <div className="mt-6 pt-4 border-t text-center">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsCustomModalOpen(true)}
                className="border-2 border-green-600 text-green-600 hover:bg-green-50 hover:border-green-700"
              >
                <Sliders className="w-5 h-5 mr-2" />
                Create Custom Package
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                Customize your sponsorship package with à la carte add-ons
              </p>
            </div>
          )}

          {/* Custom Package Builder Modal */}
          <CustomPackageBuilderModal
            isOpen={isCustomModalOpen}
            onClose={() => setIsCustomModalOpen(false)}
            onSave={handleSaveCustomPackage}
            initialDeliverables={customPackage?.selectedDeliverables || []}
            initialBasePackageTier={customPackage?.basePackageTier || 'set-core'}
          />
        </div>
      )}


      {/* Shows Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">
              {hasActiveFilters ? 'Matching Shows' : 'Upcoming Shows'}
            </h2>
          </div>
        </div>

        {displayShows.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {hasActiveFilters ?
                'No shows match your current filter criteria.' :
                'No upcoming shows scheduled.'
              }
            </p>
          </div>
        ) : (
          <>
            {user && sponsorableShows.length > 0 && (
              <ShowListHeader
                title="Shows"
                totalShows={displayShows.length}
                sponsorableShows={sponsorableShows.length}
                selectedCount={selectedCount}
                allSponsorableSelected={allSponsorableSelected}
                onSelectAll={handleSelectAll}
                isAuthenticated={true}
              />
            )}
            
            <div className="space-y-3">
              {displayShows.map((show) => {
                const audienceMatch = getAudienceMatchData(show);
                const hasPersonaFilters = activePersona && user && Object.values({
                  fanGender: activePersona.genders || [],
                  fanEthnicity: activePersona.ethnicities || [],
                  fanAge: activePersona.ageRanges || [],
                  fanIncome: activePersona.incomeLevels || [],
                  fanAttributes: activePersona.lifestyleAttributes || [],
                }).some(arr => arr.length > 0);

                // Read from reactive planPackages - automatically updates when pricing changes!
                const packageInfo = planPackages[show.id];
                const isInPlan = !!packageInfo;

                return (
                  <ShowListItem
                    key={show.id}
                    show={show}
                    isSelectable={show.sponsorable ?? true}
                    isSelected={selectedShows.has(show.id)}
                    onSelectionChange={(showId, checked) => handleShowSelection(showId, checked)}
                    onViewShow={() => handleViewShow(show)}
                    isAuthenticated={!!user}
                    // Audience matching props when persona is active
                    audienceMatchPercentage={hasPersonaFilters ? audienceMatch.percentage : undefined}
                    matchingAttributes={hasPersonaFilters ? audienceMatch.matchingAttributes : undefined}
                    showAudienceMatch={!!hasPersonaFilters}
                    // Plan status props - freshly read from localStorage each render
                    isInPlan={isInPlan}
                    planPackageTier={packageInfo?.tier}
                    planPackagePrice={packageInfo?.price}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Show Detail Modal */}
      {viewingShow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleBackToList}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto m-4 bg-white rounded-lg shadow-xl p-6">
            <ShowDetail
              show={viewingShow}
              onBack={handleBackToList}
              onAddToProposal={handleAddToProposal}
              isAuthenticated={!!user}
              showBackButton={true}
              backToText={`Back to ${artist?.name}`}
            />
          </div>
        </div>
      )}
    </div>
    </BrowseLayout>
  );
}